import React, { useState, useEffect } from 'react';
import { MapPin, Search, Filter, Store, Package2, Navigation, Clock, Users, CheckCircle, AlertTriangle, Info, RefreshCw } from 'lucide-react';

// TypeScript interfaces
interface Location {
  latitude?: number;
  longitude?: number;
  city?: string;
  country?: string;
  address?: string;
  accuracy?: number;
}

interface DeviceInfo {
  platform?: string;
  userAgent?: string;
  ipAddress?: string;
  language?: string;
}

interface ScanRecord {
  scanId?: string;
  consumerId?: string;
  timestamp: Date | string;
  location: Location;
  deviceInfo?: DeviceInfo;
  scanType?: string;
  isAuthentic?: boolean;
  validationsPassed?: string[];
}

interface TrackingScan {
  scanTime: Date | string;
  location?: Location;
  consumerId?: string;
  deviceInfo?: DeviceInfo;
  isAuthentic?: boolean;
}

interface TagStats {
  totalScans?: number;
  uniqueConsumers?: number;
  authenticatedScans?: number;
  anonymousScans?: number;
  lastScan?: Date | string;
  scanHistory?: ScanRecord[];
}

interface TagTracking {
  scans?: TrackingScan[];
  totalScans?: number;
  showing?: number;
  hasMore?: boolean;
}

interface LibraryInfo {
  name?: string;
  tagType?: string;
}

interface Tag {
  _id?: string;
  tagId: string;
  status?: string;
  businessId: string;
  productId?: string;
  storeId?: string;
  productName?: string;
  libraryId?: LibraryInfo;
  stats?: TagStats;
  tracking?: TagTracking;
}

interface Address {
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

interface GPSValidation {
  enabled?: boolean;
  radius?: number;
  strictMode?: boolean;
}

interface SecuritySettings {
  requireManagerApproval?: boolean;
  fraudDetectionLevel?: string;
  maxActivationsPerHour?: number;
}

interface StoreStats {
  totalActivations?: number;
  totalScans?: number;
  fraudAlerts?: number;
  lastActivity?: Date | string;
  todayActivations?: number;
}

interface Store {
  _id?: string;
  storeId: string;
  businessId: string;
  storeName: string;
  storeType?: string;
  latitude?: number;
  longitude?: number;
  address?: string | Address;
  manager?: string;
  email?: string;
  storePhone?: string;
  isActive?: boolean;
  gpsValidation?: GPSValidation;
  allowChipActivation?: boolean;
  allowQRActivation?: boolean;
  canActivateChip?: boolean;
  canActivateQR?: boolean;
  securitySettings?: SecuritySettings;
  stats?: StoreStats;
  distance?: number;
  distanceKm?: number;
}

interface UserLocation {
  lat: number;
  lng: number;
}

// Utility function to get current business ID from localStorage
const getCurrentBusinessId = (): string | null => {
  const businessData = localStorage.getItem('businessData');
  if (businessData) {
    try {
      const parsed = JSON.parse(businessData);
      return parsed.businessId || parsed._id;
    } catch (e) {
      console.error('Error parsing business data:', e);
    }
  }
  
  return localStorage.getItem('businessId') || 
         localStorage.getItem('currentBusinessId') || 
         null;
};

// API Service for fetching real data
const apiService = {
  async getStores(businessId: string, userLocation: UserLocation | null = null): Promise<Store[]> {
    try {
      let url = `/api/stores?businessId=${businessId}`;
      
      // Add geospatial query if user location is available
      if (userLocation && userLocation.lat && userLocation.lng) {
        url += `&near=${userLocation.lat},${userLocation.lng},50`; // 50km radius
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('businessToken')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      return data.success ? data.stores || [] : [];
    } catch (error) {
      console.error('Failed to fetch stores:', error);
      return [];
    }
  },

  async getTagTracking(businessId: string): Promise<Tag[]> {
    try {
      // Get tags with tracking data - using existing tags endpoint
      const response = await fetch(`${process.env.REACT_APP_API_URL}/tags?businessId=${businessId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('businessToken')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      return data.success ? data.tags || data.data || [] : [];
    } catch (error) {
      console.error('Failed to fetch tag tracking:', error);
      return [];
    }
  },

  async getTagTrackingDetails(tagId: string): Promise<Tag | null> {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/tags/tracking/${tagId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('businessToken')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      return data.success ? data : null;
    } catch (error) {
      console.error('Failed to fetch tag tracking details:', error);
      return null;
    }
  },

  async getNearbyStores(lat: number, lng: number, businessId: string, radius: number = 10): Promise<Store[]> {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/stores/nearby/${lat}/${lng}?radius=${radius}&businessId=${businessId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('businessToken')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      return data.success ? data.stores || [] : [];
    } catch (error) {
      console.error('Failed to fetch nearby stores:', error);
      return [];
    }
  }
};

const ConsumerStoreMapPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'stores' | 'tracking'>('stores');
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [tagTracking, setTagTracking] = useState<Tag[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('all');
  const [tagSearchQuery, setTagSearchQuery] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [locationLoading, setLocationLoading] = useState<boolean>(false);

  // Get current business ID
  const currentBusinessId = getCurrentBusinessId();

  // Get user location
  const getUserLocation = (): void => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location: UserLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(location);
          setLocationLoading(false);
          // Reload stores with location data
          loadStores(location);
        },
        (error) => {
          console.error('Error getting location:', error);
          setLocationLoading(false);
          // Load stores without location
          loadStores();
        }
      );
    } else {
      setLocationLoading(false);
      loadStores();
    }
  };

  // Load data on component mount
  useEffect(() => {
    if (currentBusinessId) {
      getUserLocation();
    } else {
      setError('No business ID found. Please log in again.');
      setLoading(false);
    }
  }, [currentBusinessId]);

  const loadStores = async (location: UserLocation | null = userLocation): Promise<void> => {
    if (!currentBusinessId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const storesData = await apiService.getStores(currentBusinessId, location);
      setStores(storesData);
    } catch (err) {
      console.error('Error loading stores:', err);
      setError('Failed to load stores. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadTagTracking = async (): Promise<void> => {
    if (!currentBusinessId) return;
    
    try {
      const tagsData = await apiService.getTagTracking(currentBusinessId);
      setTagTracking(tagsData.filter(tag => tag.status === 'activated')); // Only show activated tags
    } catch (err) {
      console.error('Error loading tag tracking:', err);
    }
  };

  const loadData = async (): Promise<void> => {
    await Promise.all([loadStores(), loadTagTracking()]);
  };

  // Load tag tracking when switching to tracking tab
  useEffect(() => {
    if (activeTab === 'tracking' && tagTracking.length === 0) {
      loadTagTracking();
    }
  }, [activeTab, tagTracking.length]);

  // Filter stores based on search and type
  const filteredStores = stores.filter(store => {
    if (!store || !currentBusinessId) return false;
    
    const matchesSearch = (store.storeName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (store.manager || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (typeof store.address === 'object' ? store.address?.street || '' : store.address || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (typeof store.address === 'object' ? store.address?.city || '' : '').toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = filterType === 'all' || store.storeType === filterType;
    
    return matchesSearch && matchesType && store.businessId === currentBusinessId;
  });

  // Filter tags based on search
  const filteredTags = tagTracking.filter(tag => {
    if (!tag || !currentBusinessId) return false;
    
    const matchesSearch = (tag.tagId || '').toLowerCase().includes(tagSearchQuery.toLowerCase()) ||
                         (tag.productName || '').toLowerCase().includes(tagSearchQuery.toLowerCase()) ||
                         (tag.libraryId?.name || '').toLowerCase().includes(tagSearchQuery.toLowerCase());
    
    return matchesSearch && tag.businessId === currentBusinessId;
  });

  const getStoreTypeColor = (type: string | undefined): string => {
    switch (type) {
      case 'flagship': return 'bg-purple-100 text-purple-800';
      case 'factory': return 'bg-orange-100 text-orange-800';
      case 'warehouse': return 'bg-blue-100 text-blue-800';
      case 'distributor': return 'bg-indigo-100 text-indigo-800';
      case 'outlet': return 'bg-green-100 text-green-800';
      case 'retailer': return 'bg-gray-100 text-gray-800';
      case 'office': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatAddress = (store: Store): string => {
    if (store.address && typeof store.address === 'object') {
      const parts: string[] = [];
      if (store.address.street) parts.push(store.address.street);
      if (store.address.city) parts.push(store.address.city);
      if (store.address.state) parts.push(store.address.state);
      if (store.address.country && store.address.country !== 'US') parts.push(store.address.country);
      if (parts.length > 0) return parts.join(', ');
    }
    
    if (typeof store.address === 'string') return store.address;
    
    // Fallback to coordinates
    return `${store.latitude?.toFixed(4)}, ${store.longitude?.toFixed(4)}`;
  };

  const formatDistance = (distance: number | undefined): string => {
    if (!distance && distance !== 0) return 'Unknown';
    if (distance < 1000) return `${Math.round(distance)}m`;
    return `${(distance / 1000).toFixed(1)}km`;
  };

  const handleTagDetails = async (tag: Tag): Promise<void> => {
    try {
      const details = await apiService.getTagTrackingDetails(tag.tagId);
      if (details) {
        setSelectedTag({ ...tag, ...details });
      } else {
        setSelectedTag(tag);
      }
    } catch (error) {
      console.error('Error loading tag details:', error);
      setSelectedTag(tag);
    }
  };

  if (!currentBusinessId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Business ID Not Found</h2>
          <p className="text-gray-600">Please log in again to access store and tracking data.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Loading Store Data</h2>
          <p className="text-gray-600">
            {locationLoading ? 'Getting your location...' : 'Fetching stores and tracking information...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => getUserLocation()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <MapPin className="w-8 h-8 mr-3 text-blue-600" />
                Store Locator & Tag Tracking
              </h1>
              <div className="flex items-center space-x-4 mt-1">
                <p className="text-gray-600">Business ID: <code className="text-xs bg-gray-100 px-2 py-1 rounded">{currentBusinessId}</code></p>
                {userLocation && (
                  <p className="text-green-600 text-sm flex items-center">
                    <Navigation className="w-4 h-4 mr-1" />
                    Location enabled
                  </p>
                )}
              </div>
            </div>
            <div className="flex space-x-2">
              {!userLocation && (
                <button
                  onClick={getUserLocation}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
                  disabled={locationLoading}
                >
                  {locationLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Navigation className="w-4 h-4 mr-2" />
                  )}
                  Get Location
                </button>
              )}
              <button
                onClick={loadData}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                disabled={loading}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Refresh
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('stores')}
              className={`flex-1 flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'stores'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Store className="w-4 h-4 mr-2" />
              Store Map ({filteredStores.length})
            </button>
            <button
              onClick={() => setActiveTab('tracking')}
              className={`flex-1 flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'tracking'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Package2 className="w-4 h-4 mr-2" />
              Tag Tracking ({filteredTags.length})
            </button>
          </div>
        </div>

        {/* Store Map Tab */}
        {activeTab === 'stores' && (
          <>
            {/* Search and Filter Controls */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search stores by name, manager, or location..."
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Types</option>
                    <option value="flagship">Flagship</option>
                    <option value="retailer">Retailer</option>
                    <option value="outlet">Outlet</option>
                    <option value="warehouse">Warehouse</option>
                    <option value="factory">Factory</option>
                    <option value="distributor">Distributor</option>
                    <option value="office">Office</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Store Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="text-2xl font-bold text-blue-600">{filteredStores.length}</div>
                <div className="text-sm text-gray-600">Total Stores</div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="text-2xl font-bold text-green-600">
                  {filteredStores.filter(s => s.isActive).length}
                </div>
                <div className="text-sm text-gray-600">Active Stores</div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="text-2xl font-bold text-orange-600">
                  {filteredStores.filter(s => s.gpsValidation?.enabled).length}
                </div>
                <div className="text-sm text-gray-600">GPS Enabled</div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="text-2xl font-bold text-purple-600">
                  {filteredStores.filter(s => s.canActivateChip || s.canActivateQR || (s.allowChipActivation && s.allowQRActivation)).length}
                </div>
                <div className="text-sm text-gray-600">Can Activate</div>
              </div>
            </div>

            {/* Store Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStores.map((store) => (
                <div key={store.storeId || store._id} className="bg-white rounded-2xl shadow-sm border p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900 truncate pr-2">{store.storeName}</h3>
                    <div className="flex flex-col items-end space-y-1">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStoreTypeColor(store.storeType)}`}>
                        {store.storeType || 'retailer'}
                      </span>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        store.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                      }`}>
                        {store.isActive ? 'Open' : 'Closed'}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="truncate">{formatAddress(store)}</span>
                    </div>
                    <div className="flex items-center">
                      <Users className="w-4 h-4 mr-2 text-gray-400" />
                      <span>{store.manager || 'No manager assigned'}</span>
                    </div>
                    {store.distance !== undefined && (
                      <div className="flex items-center">
                        <Navigation className="w-4 h-4 mr-2 text-gray-400" />
                        <span>{formatDistance(store.distance)} away</span>
                      </div>
                    )}
                  </div>

                  {/* Store Stats */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="text-center">
                        <div className="font-bold text-blue-600">{store.stats?.totalActivations || 0}</div>
                        <div className="text-gray-600">Activations</div>
                      </div>
                      <div className="text-center">
                        <div className="font-bold text-green-600">{store.stats?.totalScans || 0}</div>
                        <div className="text-gray-600">Scans</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between text-xs mb-4">
                    <div className="flex items-center">
                      <span className="font-medium">GPS:</span>
                      <span className={`ml-1 ${store.gpsValidation?.enabled ? 'text-green-600' : 'text-red-600'}`}>
                        {store.gpsValidation?.enabled ? `${store.gpsValidation.radius}m` : "Off"}
                      </span>
                    </div>
                    <div className="flex space-x-3">
                      <span className={`${store.canActivateChip || store.allowChipActivation ? 'text-green-600' : 'text-red-600'}`}>
                        Chip: {store.canActivateChip || store.allowChipActivation ? "✓" : "✗"}
                      </span>
                      <span className={`${store.canActivateQR || store.allowQRActivation ? 'text-green-600' : 'text-red-600'}`}>
                        QR: {store.canActivateQR || store.allowQRActivation ? "✓" : "✗"}
                      </span>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => setSelectedStore(store)}
                      className="flex-1 bg-blue-50 text-blue-600 px-3 py-2 rounded-lg hover:bg-blue-100 text-sm transition-colors"
                    >
                      👁️ Details
                    </button>
                    <button
                      onClick={() => {
                        if (store.latitude && store.longitude) {
                          const url = `https://maps.google.com/?q=${store.latitude},${store.longitude}`;
                          window.open(url, '_blank');
                        }
                      }}
                      className="flex-1 bg-green-50 text-green-600 px-3 py-2 rounded-lg hover:bg-green-100 text-sm transition-colors"
                      disabled={!store.latitude || !store.longitude}
                    >
                      🗺️ Navigate
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Empty State for Stores */}
            {filteredStores.length === 0 && (
              <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No stores found</h3>
                <p className="text-gray-600">
                  {stores.length === 0 
                    ? 'No stores have been created for this business yet.' 
                    : 'Try adjusting your search terms or filters.'
                  }
                </p>
              </div>
            )}
          </>
        )}

        {/* Tag Tracking Tab */}
        {activeTab === 'tracking' && (
          <>
            {/* Tag Search Controls */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={tagSearchQuery}
                      onChange={(e) => setTagSearchQuery(e.target.value)}
                      placeholder="Search tags by ID, product name, or library..."
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <button
                  onClick={loadTagTracking}
                  className="bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 flex items-center"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Refresh
                </button>
              </div>
            </div>

            {/* Tag Tracking Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="text-2xl font-bold text-blue-600">{filteredTags.length}</div>
                <div className="text-sm text-gray-600">Tracked Tags</div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="text-2xl font-bold text-green-600">
                  {filteredTags.reduce((sum, tag) => sum + (tag.stats?.totalScans || 0), 0)}
                </div>
                <div className="text-sm text-gray-600">Total Scans</div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="text-2xl font-bold text-orange-600">
                  {filteredTags.reduce((sum, tag) => sum + (tag.stats?.uniqueConsumers || 0), 0)}
                </div>
                <div className="text-sm text-gray-600">Unique Consumers</div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="text-2xl font-bold text-purple-600">
                  {filteredTags.filter(tag => tag.status === 'activated').length}
                </div>
                <div className="text-sm text-gray-600">Activated Tags</div>
              </div>
            </div>

            {/* Tag Tracking Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredTags.map((tag) => (
                <div key={tag.tagId || tag._id} className="bg-white rounded-2xl shadow-sm border p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{tag.tagId}</h3>
                      <p className="text-sm text-gray-600">{tag.libraryId?.name || 'Product not specified'}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        tag.status === 'activated' ? 'bg-green-100 text-green-800' : 
                        tag.status === 'recalled' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {tag.status || 'inactive'}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Store ID:</span>
                      <span className="truncate ml-2 font-mono text-xs">{tag.storeId || 'Not assigned'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Total Scans:</span>
                      <span className="text-blue-600 font-bold">{tag.stats?.totalScans || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Unique Consumers:</span>
                      <span className="text-green-600 font-bold">{tag.stats?.uniqueConsumers || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Last Scan:</span>
                      <span className="text-gray-800">
                        {tag.stats?.lastScan ? new Date(tag.stats.lastScan).toLocaleDateString() : 'Never'}
                      </span>
                    </div>
                  </div>

                  {/* Recent Locations from scan history */}
                  {tag.stats?.scanHistory && tag.stats.scanHistory.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <MapPin className="w-4 h-4 mr-1" />
                        Recent Locations ({tag.stats.scanHistory.filter(scan => scan.location?.latitude && scan.location?.longitude).length})
                      </h4>
                      <div className="space-y-1">
                        {tag.stats.scanHistory
                          .filter(scan => scan.location?.latitude && scan.location?.longitude)
                          .slice(0, 2)
                          .map((scan: ScanRecord, index: number) => (
                            <div key={index} className="flex items-center justify-between text-xs text-gray-600 bg-gray-50 rounded p-2">
                              <span>{scan.location.city || 'Unknown'}, {scan.location.country || 'Unknown'}</span>
                              <span className="text-gray-400">
                                {new Date(scan.timestamp).toLocaleDateString()}
                              </span>
                            </div>
                          ))
                        }
                        {tag.stats.scanHistory.filter(scan => scan.location?.latitude && scan.location?.longitude).length > 2 && (
                          <div className="text-xs text-blue-600 text-center">
                            +{tag.stats.scanHistory.filter(scan => scan.location?.latitude && scan.location?.longitude).length - 2} more locations
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleTagDetails(tag)}
                      className="flex-1 bg-blue-50 text-blue-600 px-3 py-2 rounded-lg hover:bg-blue-100 text-sm transition-colors"
                    >
                      📍 View Tracking
                    </button>
                    <button
                      onClick={() => {
                        // Show latest location on map if available
                        const latestLocation = tag.stats?.scanHistory?.find(scan => scan.location?.latitude && scan.location?.longitude);
                        if (latestLocation) {
                          const url = `https://maps.google.com/?q=${latestLocation.location.latitude},${latestLocation.location.longitude}`;
                          window.open(url, '_blank');
                        }
                      }}
                      className="flex-1 bg-green-50 text-green-600 px-3 py-2 rounded-lg hover:bg-green-100 text-sm transition-colors"
                      disabled={!tag.stats?.scanHistory?.some(scan => scan.location?.latitude && scan.location?.longitude)}
                    >
                      🗺️ Show on Map
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Empty State for Tags */}
            {filteredTags.length === 0 && (
              <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                <Package2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No tracked tags found</h3>
                <p className="text-gray-600">
                  {tagTracking.length === 0 
                    ? 'No tags have been scanned for tracking yet.' 
                    : 'Try adjusting your search terms.'
                  }
                </p>
              </div>
            )}
          </>
        )}

        {/* Store Detail Modal */}
        {selectedStore && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">{selectedStore.storeName}</h3>
                  <button
                    onClick={() => setSelectedStore(null)}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Contact Information</h4>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p><strong>Manager:</strong> {selectedStore.manager || 'Not assigned'}</p>
                        <p><strong>Email:</strong> {selectedStore.email || 'Not provided'}</p>
                        <p><strong>Phone:</strong> {selectedStore.storePhone || 'Not provided'}</p>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Store Details</h4>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p><strong>Type:</strong> {selectedStore.storeType || 'retailer'}</p>
                        <p><strong>Status:</strong> 
                          <span className={`ml-1 ${selectedStore.isActive ? 'text-green-600' : 'text-red-600'}`}>
                            {selectedStore.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </p>
                        <p><strong>Store ID:</strong> <code className="text-xs">{selectedStore.storeId}</code></p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Location</h4>
                    <div className="text-sm text-gray-600">
                      <p><strong>Address:</strong> {formatAddress(selectedStore)}</p>
                      <p><strong>Coordinates:</strong> {selectedStore.latitude?.toFixed(4)}, {selectedStore.longitude?.toFixed(4)}</p>
                      {selectedStore.distance !== undefined && (
                        <p><strong>Distance:</strong> {formatDistance(selectedStore.distance)} from your location</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">GPS & Activation Settings</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="space-y-1">
                        <p><strong>GPS Validation:</strong>
                          <span className={`ml-1 ${selectedStore.gpsValidation?.enabled ? 'text-green-600' : 'text-red-600'}`}>
                            {selectedStore.gpsValidation?.enabled ? `Enabled (${selectedStore.gpsValidation.radius}m)` : 'Disabled'}
                          </span>
                        </p>
                        {selectedStore.gpsValidation?.strictMode && (
                          <p className="text-orange-600 text-xs">Strict Mode: 10m precision required</p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <p><strong>Chip Activation:</strong>
                          <span className={`ml-1 ${selectedStore.canActivateChip || selectedStore.allowChipActivation ? 'text-green-600' : 'text-red-600'}`}>
                            {selectedStore.canActivateChip || selectedStore.allowChipActivation ? 'Allowed' : 'Blocked'}
                          </span>
                        </p>
                        <p><strong>QR Activation:</strong>
                          <span className={`ml-1 ${selectedStore.canActivateQR || selectedStore.allowQRActivation ? 'text-green-600' : 'text-red-600'}`}>
                            {selectedStore.canActivateQR || selectedStore.allowQRActivation ? 'Allowed' : 'Blocked'}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Store Statistics */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Statistics</h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="bg-blue-50 rounded-lg p-3">
                        <div className="text-xl font-bold text-blue-600">{selectedStore.stats?.totalActivations || 0}</div>
                        <div className="text-blue-700">Total Activations</div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3">
                        <div className="text-xl font-bold text-green-600">{selectedStore.stats?.totalScans || 0}</div>
                        <div className="text-green-700">Total Scans</div>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-3">
                        <div className="text-xl font-bold text-orange-600">{selectedStore.stats?.todayActivations || 0}</div>
                        <div className="text-orange-700">Today's Activations</div>
                      </div>
                    </div>
                  </div>

                  {/* Security Settings */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Security Settings</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p><strong>Fraud Detection:</strong> {selectedStore.securitySettings?.fraudDetectionLevel || 'medium'}</p>
                      <p><strong>Max Activations/Hour:</strong> {selectedStore.securitySettings?.maxActivationsPerHour || 50}</p>
                      <p><strong>Manager Approval:</strong> {selectedStore.securitySettings?.requireManagerApproval ? 'Required' : 'Not required'}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
                  <button
                    onClick={() => {
                      if (selectedStore.latitude && selectedStore.longitude) {
                        const url = `https://maps.google.com/?q=${selectedStore.latitude},${selectedStore.longitude}`;
                        window.open(url, '_blank');
                      }
                    }}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                    disabled={!selectedStore.latitude || !selectedStore.longitude}
                  >
                    🗺️ Open in Maps
                  </button>
                  <button
                    onClick={() => setSelectedStore(null)}
                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tag Detail Modal */}
        {selectedTag && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{selectedTag.tagId}</h3>
                    <p className="text-gray-600">{selectedTag.libraryId?.name || 'Product not specified'}</p>
                  </div>
                  <button
                    onClick={() => setSelectedTag(null)}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Tag Information</h4>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p><strong>Status:</strong> 
                          <span className={`ml-1 ${selectedTag.status === 'activated' ? 'text-green-600' : 'text-gray-600'}`}>
                            {selectedTag.status || 'inactive'}
                          </span>
                        </p>
                        <p><strong>Business ID:</strong> <code className="text-xs">{selectedTag.businessId}</code></p>
                        <p><strong>Store ID:</strong> <code className="text-xs">{selectedTag.storeId || 'Not assigned'}</code></p>
                        <p><strong>Product ID:</strong> <code className="text-xs">{selectedTag.productId || 'Not assigned'}</code></p>
                        <p><strong>Library:</strong> {selectedTag.libraryId?.name || 'Not assigned'}</p>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Scan Statistics</h4>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p><strong>Total Scans:</strong> {selectedTag.stats?.totalScans || 0}</p>
                        <p><strong>Unique Consumers:</strong> {selectedTag.stats?.uniqueConsumers || 0}</p>
                        <p><strong>Authenticated Scans:</strong> {selectedTag.stats?.authenticatedScans || 0}</p>
                        <p><strong>Anonymous Scans:</strong> {selectedTag.stats?.anonymousScans || 0}</p>
                        <p><strong>Last Scan:</strong> {selectedTag.stats?.lastScan ? new Date(selectedTag.stats.lastScan).toLocaleString() : 'Never'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Tracking History */}
                  {selectedTag.tracking?.scans && selectedTag.tracking.scans.length > 0 ? (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-4">Recent Tracking History</h4>
                      <div className="max-h-64 overflow-y-auto space-y-2">
                        {selectedTag.tracking.scans.slice(0, 10).map((scan: TrackingScan, index: number) => (
                          <div key={index} className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium text-sm">
                                  {scan.location?.city || 'Unknown'}, {scan.location?.country || 'Unknown'}
                                </div>
                                <div className="text-xs text-gray-500">
                                  Consumer: {scan.consumerId || 'Anonymous'} • Platform: {scan.deviceInfo?.platform || 'Unknown'}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-medium">{new Date(scan.scanTime).toLocaleDateString()}</div>
                                <div className="text-xs text-gray-500">{new Date(scan.scanTime).toLocaleTimeString()}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                        {selectedTag.tracking.hasMore && (
                          <div className="text-center text-sm text-blue-600">
                            Showing {selectedTag.tracking.showing} of {selectedTag.tracking.totalScans} total scans
                          </div>
                        )}
                      </div>
                    </div>
                  ) : selectedTag.stats?.scanHistory && selectedTag.stats.scanHistory.length > 0 ? (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-4">Scan History</h4>
                      <div className="max-h-64 overflow-y-auto space-y-2">
                        {selectedTag.stats.scanHistory.slice(0, 10).map((scan: ScanRecord, index: number) => (
                          <div key={index} className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium text-sm">
                                  {scan.location?.city || 'Unknown'}, {scan.location?.country || 'Unknown'}
                                </div>
                                <div className="text-xs text-gray-500">
                                  Consumer: {scan.consumerId || 'Anonymous'} • Type: {scan.scanType || 'scan'}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-medium">{new Date(scan.timestamp).toLocaleDateString()}</div>
                                <div className="text-xs text-gray-500">{new Date(scan.timestamp).toLocaleTimeString()}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Package2 className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500">No tracking history available</p>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
                  <button
                    onClick={() => {
                      // Show latest location on map if available
                      const latestScan = selectedTag.tracking?.scans?.[0] || selectedTag.stats?.scanHistory?.[0];
                      if (latestScan?.location?.latitude && latestScan?.location?.longitude) {
                        const url = `https://maps.google.com/?q=${latestScan.location.latitude},${latestScan.location.longitude}`;
                        window.open(url, '_blank');
                      }
                    }}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                    disabled={!(selectedTag.tracking?.scans?.[0]?.location?.latitude || selectedTag.stats?.scanHistory?.[0]?.location?.latitude)}
                  >
                    🗺️ Show Latest Location
                  </button>
                  <button
                    onClick={() => setSelectedTag(null)}
                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ConsumerStoreMapPage;